<script setup lang="ts">
import { computed, onMounted, ref, shallowRef } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Eye, EyeOff, KeyRound } from "lucide-vue-next";
import AuthPageShell from "@/components/auth/AuthPageShell.vue";
import ExternalMfaChallengeForm from "@/components/auth/ExternalMfaChallenge.vue";
import ExternalTotpEnrollment from "@/components/auth/ExternalTotpEnrollment.vue";
import LogoMark from "@/components/LogoMark.vue";
import { api } from "@/services/api";
import {
  completeAccountSetup,
  completeExternalMfaSignIn,
  finishExternalTotpEnrollment,
  refreshExternalIdentity,
  refreshAuthToken,
  signInExternal,
  signOutUser,
  startExternalTotpEnrollment,
  type ExternalMfaChallenge,
  type ExternalTotpEnrollment as ExternalTotpEnrollmentState,
  verifyAccountSetupCode,
} from "@/services/firebase";
import { useWorkspaceStore } from "@/stores/workspace";
import { consumeInvitationSecret } from "@/utils/invitations";
import { externalMfaErrorMessage } from "@/utils/auth-errors";

const route = useRoute();
const router = useRouter();
const workspace = useWorkspaceStore();
const actionCode =
  typeof route.query.oobCode === "string" ? route.query.oobCode : "";
const invitation = consumeInvitationSecret();
const email = ref("");
const password = ref("");
const confirmation = ref("");
const showPassword = ref(false);
const state = ref<"checking" | "ready" | "submitting" | "invalid" | "error">(
  "checking",
);
const passwordCompleted = ref(false);
const message = ref("");
const mfaChallenge = ref<ExternalMfaChallenge | null>(null);
const mfaEnrollment = shallowRef<ExternalTotpEnrollmentState | null>(null);

const passwordIssue = computed(() => {
  if (!password.value) return "";
  if (password.value.length < 15)
    return "Use at least 15 characters. A long passphrase works well.";
  if (confirmation.value && password.value !== confirmation.value)
    return "The passwords do not match.";
  return "";
});

onMounted(async () => {
  if (!actionCode || !invitation) {
    state.value = "invalid";
    return;
  }
  try {
    email.value = await verifyAccountSetupCode(actionCode);
    state.value = "ready";
  } catch {
    state.value = "invalid";
  }
});

async function redeemInvitation() {
  if (!invitation) return;
  const result = (await api.redeemInvitation(invitation)) as {
    route: string;
  };
  await refreshAuthToken();
  await workspace.refreshFromApi();
  await router.replace(result.route);
}

async function submit() {
  if (
    !invitation ||
    !email.value ||
    password.value.length < 15 ||
    password.value !== confirmation.value
  )
    return;
  state.value = "submitting";
  message.value = "";
  try {
    if (!passwordCompleted.value) {
      await completeAccountSetup(actionCode, password.value);
      passwordCompleted.value = true;
    }
    const signIn = await signInExternal(email.value, password.value);
    if (signIn.status === "mfa-required") {
      mfaChallenge.value = signIn.challenge;
      state.value = "ready";
      return;
    }
    if (signIn.status === "mfa-enrollment-required") {
      await api.prepareInvitationMfa(invitation);
      await refreshExternalIdentity();
      mfaEnrollment.value = await startExternalTotpEnrollment(password.value);
      state.value = "ready";
      return;
    }
    await redeemInvitation();
  } catch (setupError) {
    state.value = "error";
    message.value = mfaEnrollment.value
      ? externalMfaErrorMessage(setupError, "enroll")
      : passwordCompleted.value
        ? "Your password is saved, but account setup could not continue. Try again. If the link was replaced or revoked, ask the project owner for a new invitation."
        : "We could not set up your account. Your invitation may have expired or been replaced. Ask the project owner to send a new invitation.";
  }
}

async function completeEnrollment(code: string) {
  if (!mfaEnrollment.value) return;
  state.value = "submitting";
  message.value = "";
  try {
    await finishExternalTotpEnrollment(mfaEnrollment.value, code);
    mfaEnrollment.value = null;
    await signOutUser();
    const signIn = await signInExternal(email.value, password.value);
    if (signIn.status !== "mfa-required")
      throw new Error("planner/mfa-challenge-required");
    mfaChallenge.value = signIn.challenge;
    state.value = "ready";
  } catch (enrollmentError) {
    state.value = mfaEnrollment.value ? "ready" : "error";
    message.value = externalMfaErrorMessage(enrollmentError, "enroll");
  }
}

async function verifyExternalMfa(value: { factorUid: string; code: string }) {
  if (!mfaChallenge.value) return;
  state.value = "submitting";
  message.value = "";
  try {
    await completeExternalMfaSignIn(
      mfaChallenge.value,
      value.factorUid,
      value.code,
    );
    await redeemInvitation();
  } catch (mfaError) {
    state.value = "ready";
    message.value = externalMfaErrorMessage(mfaError, "sign-in");
  }
}

async function cancelMfa() {
  mfaEnrollment.value = null;
  mfaChallenge.value = null;
  message.value =
    "Your password is saved. Sign in again to finish securing your account.";
  state.value = "error";
  await signOutUser().catch(() => undefined);
}
</script>

<template>
  <AuthPageShell labelled-by="setup-title">
    <div class="setup-content">
      <div class="setup-heading">
        <LogoMark class="setup-mark" tone="dark" />
        <div class="setup-icon" aria-hidden="true"><KeyRound :size="22" /></div>
        <h2 id="setup-title">Set up your account</h2>
        <p>
          Choose a password, then connect an authenticator app before accepting
          your invitation.
        </p>
      </div>

      <p v-if="state === 'checking'" class="status-copy" role="status">
        Checking your secure setup link…
      </p>

      <div
        v-else-if="state === 'invalid'"
        class="message message--error"
        role="alert"
      >
        <strong>This setup link is no longer valid.</strong>
        <span>Ask the project owner to resend your invitation.</span>
      </div>

      <ExternalMfaChallengeForm
        v-else-if="mfaChallenge"
        :challenge="mfaChallenge"
        :busy="state === 'submitting'"
        :error="message"
        action-label="Verify and accept invitation"
        @verify="verifyExternalMfa"
        @cancel="cancelMfa"
      />

      <ExternalTotpEnrollment
        v-else-if="mfaEnrollment"
        :enrollment="mfaEnrollment"
        :busy="state === 'submitting'"
        :error="message"
        action-label="Verify and continue"
        @verify="completeEnrollment"
        @cancel="cancelMfa"
      />

      <form v-else class="setup-form" @submit.prevent="submit">
        <div class="field">
          <label for="setup-email">Email</label>
          <input
            id="setup-email"
            class="input"
            type="email"
            :value="email"
            autocomplete="username"
            readonly
          />
          <p class="helper">This is the email address that was invited.</p>
        </div>
        <div class="field">
          <label for="setup-password">Password</label>
          <div class="password-field">
            <input
              id="setup-password"
              v-model="password"
              class="input"
              :type="showPassword ? 'text' : 'password'"
              autocomplete="new-password"
              minlength="15"
              required
            />
            <button
              class="password-toggle"
              type="button"
              :aria-label="showPassword ? 'Hide password' : 'Show password'"
              :aria-pressed="showPassword"
              @click="showPassword = !showPassword"
            >
              <EyeOff v-if="showPassword" :size="17" />
              <Eye v-else :size="17" />
            </button>
          </div>
          <p class="helper">
            Use at least 15 characters. Pasting from a password manager is
            supported.
          </p>
        </div>
        <div class="field">
          <label for="setup-confirmation">Confirm password</label>
          <input
            id="setup-confirmation"
            v-model="confirmation"
            class="input"
            :type="showPassword ? 'text' : 'password'"
            autocomplete="new-password"
            minlength="15"
            required
          />
        </div>
        <p v-if="passwordIssue" class="field-error" role="alert">
          {{ passwordIssue }}
        </p>
        <div
          v-if="state === 'error'"
          class="message message--error"
          role="alert"
        >
          {{ message }}
        </div>
        <button
          class="button button--primary setup-submit"
          type="submit"
          :disabled="
            state === 'submitting' ||
            password.length < 15 ||
            password !== confirmation
          "
        >
          {{
            state === "submitting"
              ? "Setting up account…"
              : passwordCompleted
                ? "Continue account setup"
                : "Set password and continue"
          }}
        </button>
      </form>

      <RouterLink class="back-link" to="/login">Back to sign in</RouterLink>
    </div>
  </AuthPageShell>
</template>

<style scoped>
.setup-content {
  width: min(430px, 100%);
  display: grid;
  gap: 24px;
}
.setup-heading {
  display: grid;
  justify-items: center;
  text-align: center;
}
.setup-mark {
  margin-bottom: 20px;
}
.setup-mark :deep(.logo-mark__wordmark) {
  width: 132px;
}
.setup-icon {
  width: 44px;
  height: 44px;
  margin-bottom: 14px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  color: var(--brand-strong);
  background: var(--brand-soft);
}
.setup-heading h2 {
  font-size: 25px;
}
.setup-heading p,
.status-copy {
  margin-top: 7px;
  color: var(--text-soft);
}
.setup-form {
  display: grid;
  gap: 16px;
}
.setup-form .input {
  min-height: 44px;
}
.input[readonly] {
  color: var(--text-soft);
  background: var(--sheet-strong);
}
.password-field {
  position: relative;
}
.password-field .input {
  padding-right: 44px;
}
.password-toggle {
  position: absolute;
  top: 1px;
  right: 1px;
  width: 44px;
  height: calc(100% - 2px);
  display: grid;
  place-items: center;
  border-radius: 0 5px 5px 0;
  color: var(--text-soft);
  background: transparent;
  cursor: pointer;
}
.password-toggle:hover {
  color: var(--text);
  background: var(--sheet-strong);
}
.field-error {
  color: var(--danger);
  font-size: 12px;
}
.message {
  display: grid;
  gap: 3px;
  padding: 12px;
  border-radius: 7px;
}
.message--error {
  color: #7d2e2e;
  background: var(--danger-soft);
}
.setup-submit {
  min-height: 44px;
}
.back-link {
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-self: center;
  color: var(--text-soft);
  font-size: 12px;
  text-decoration: underline;
  text-underline-offset: 3px;
}
</style>
